import { defaultSortStateStaffTable, type NurseInShiftWithWage } from "../../../components/StaffTable/StaffTable";
import { dailyViewStaffTableSortingKey, localStorageService } from "../../../services/LocalStorageService";
import type { SortingState } from "../../../utils/multiColumnSorting";
import type { PrintoutColumn } from "../types";
import type { SortProjection } from "./sortKey";

/**
 * The saved DaySchedule UI table sort state uses `NurseInShiftWithWage` field names. This map
 * translates those field names into the printout's `SortProjection` field names so that the
 * configured-sort and fallback paths produce the same downstream type (mapping §5.1 + §5.8).
 *
 * Untranslated fields (e.g. `shiftDeltaHours`, `id`, `wage`) are silently dropped — they have
 * no printout equivalent.
 */
const TABLE_FIELD_TO_PROJECTION_KEY: Partial<Record<keyof NurseInShiftWithWage, keyof SortProjection>> = {
	name: "name",
	role: "role",
	jobName: "role",
	location: "location",
	startTime: "shiftTime",
	endTime: "shiftEnd",
};

const translateTableSortState = (state: SortingState<NurseInShiftWithWage>[]): SortingState<SortProjection>[] => {
	const result: SortingState<SortProjection>[] = [];
	for (const entry of state) {
		const seen = new Set<keyof SortProjection>();
		const translated: Array<keyof SortProjection> = [];
		for (const col of entry.sortColumns) {
			const mapped = TABLE_FIELD_TO_PROJECTION_KEY[col];
			if (mapped && !seen.has(mapped)) {
				seen.add(mapped);
				translated.push(mapped);
			}
		}
		if (translated.length > 0) {
			result.push({ sortColumns: translated, sortOrder: entry.sortOrder, label: entry.label });
		}
	}
	return result;
};

/**
 * Convert the user's *Sort by* list into the `SortingState<SortProjection>[]` shape that the
 * unified row sort consumes (mapping §5.1 source 1).
 *
 * Only enabled columns participate. Order is preserved — each enabled column becomes one
 * sort entry with its own direction.
 */
const fromConfiguredSortBy = (sortBy: PrintoutColumn[]): SortingState<SortProjection>[] => {
	return sortBy
		.filter((col) => col.enabled)
		.map<SortingState<SortProjection>>((col) => {
			// `shiftTime` sort considers both start AND end (earlier end first on asc, later end
			// first on desc). Without this, two rows with the same start but different ends would
			// tie on shiftTime and fall through to the next user-configured column, so a 06:30-14:30
			// and 06:30-15:00 would be sorted by name instead of grouped by their window.
			if (col.type === "shiftTime") {
				return {
					sortColumns: ["shiftTime", "shiftEnd"],
					sortOrder: col.sortDirection === "desc" ? "desc" : "asc",
				};
			}
			return {
				sortColumns: [col.type as keyof SortProjection],
				sortOrder: col.sortDirection === "desc" ? "desc" : "asc",
			};
		});
};

interface SelectSortSourceArgs {
	sortBy: PrintoutColumn[] | undefined;
	userId: string;
}

/**
 * Pick the sort source (mapping §5.1) and return it as a unified `SortingState<SortProjection>[]`.
 *
 * - If the user's *Sort by* has any enabled column, that wins.
 * - Otherwise, fall back to the DaySchedule UI table's current sort state from local storage,
 *   defaulting to `defaultSortStateStaffTable` if nothing is saved. Field names are translated
 *   to projection keys via {@link TABLE_FIELD_TO_PROJECTION_KEY}.
 *
 * The fallback is intentional product behavior — the printout matches the on-screen order
 * when no explicit printout sort is configured.
 */
export const selectSortSource = ({ sortBy, userId }: SelectSortSourceArgs): SortingState<SortProjection>[] => {
	const configured = sortBy ? fromConfiguredSortBy(sortBy) : [];
	if (configured.length > 0) return configured;

	const saved = localStorageService.getTableSortingState<NurseInShiftWithWage>(dailyViewStaffTableSortingKey, userId) ?? defaultSortStateStaffTable;
	return translateTableSortState(saved);
};
